import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import type { IPopupProps } from '~/src/features/router/order-card';
import { CAROUSEL_STATION_COUNT_SLIDES } from '~/src/features/yandex-landing/constants';
import type { TCarouselImgUrl, TDataLayer, TSort } from '~/src/features/yandex-landing/utils/types';

/**
 * Получить массив с id для CarouselStationData
 *
 * @function
 * @name utils.getIdsForCarouselStation
 * @returns {number[]}
 */
export const getIdsForCarouselStation = (): number[] => [...Array(CAROUSEL_STATION_COUNT_SLIDES)].map((_, index) => index++);

/**
 * Данные для результирующего попапа
 *
 * @function
 * @name utils.getPopupResultData
 * @param {boolean} result - результат запроса
 * @param {string} message - сообщение ответа
 * @returns {Omit<IPopupProps, 'isOrderOpened'>}
 */
export const getPopupResultData = ({ result, message } : { result: boolean, message: string }): Omit<IPopupProps, 'isOrderOpened'> => {
    return {
        isResultOpened: true,
        resultType: result ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
        resultTitle: result ? 'Заявка отправлена' : 'Произошла ошибка',
        resultText: message || (result ? 'Мы свяжемся с вами в ближайшее время' : 'Что-то пошло не так, попробуйте позже.'),
    };
};

/**
 * Получить конечные позиции карточки в карусели - секция каталог
 *
 * @name data.getEndCarouselRowPos
 * @param {number} countCards - максимальное кол-во карточек ЯСтанций
 * @type {number[]}
 */
export const getEndCarouselRowPos = (countCards: number): number[] => {
    const maxCountInRow = 4;
    const endPosCount = Math.ceil(countCards / maxCountInRow);
    const prevCorrection = 1;
    return (Array.from(Array(endPosCount)).reduce((newArr, _, key) => {
        newArr.push(key ? newArr[key - prevCorrection] + maxCountInRow : maxCountInRow);
        return newArr;
    }, []));
};

/**
 * Получить начальные позиции карточки в карусели - секция каталог
 *
 * @name data.getStartCarouselRowPos
 * @param {number} countCards - максимальное кол-во карточек ЯСтанций
 * @type {number[]}
 */
export const getStartCarouselRowPos = (countCards: number): number[] => {
    const defaultPos = 0;
    const startCarouselRowPos = getEndCarouselRowPos(countCards);
    startCarouselRowPos.unshift(defaultPos);
    return startCarouselRowPos;
};

/**
 * Получить массив-шаблон для текущего кол-ва карточек, для правильного построения блоков карусели
 * [4] - один блок 4 карточки, [4,3] - два блока, второй из 3 элементов, [4,4,2] - три блока, третий из 2 элементов и тд.
 *
 * @function
 * @name utils.getArrTemplateForCountCards
 * @param {IShopItem[]} yandexStationData - данные карточек
 * @returns {number[]}
 */
export const getArrTemplateForCountCards = (yandexStationData: IEquipment[]): number[] => {
    const arrTemplate = [];
    const zero = 0;
    const nextPos = 1;
    const cardsLength = yandexStationData.length;
    const endCarouselRowPos = getEndCarouselRowPos(cardsLength);
    const [maxCountFirstLine] = endCarouselRowPos;

    if (yandexStationData.length) {
        arrTemplate.push(maxCountFirstLine);
    }

    [...endCarouselRowPos.keys()].forEach((key) => {
        if (cardsLength > endCarouselRowPos[key]) {
            arrTemplate.push(maxCountFirstLine -
                ((endCarouselRowPos[key + nextPos] - cardsLength) < zero
                    ? zero
                    : (endCarouselRowPos[key + nextPos] - cardsLength)));
        }
    });

    return arrTemplate;
};


/**
 * Получить массив параметров для CarouselStationData (первый блок отличный от остальных)
 *
 * @function
 * @name utils.getDataOfOneDiffParams
 * @param {string} soleParam - параметр отличающийся от других
 * @param {string} otherParam - остальные параметры
 * @returns {string[]}
 */
export const getDataOfOneDiffParams = ({ soleParam, otherParam }: Record<string, string>): string[] => {
    const startPos = 1;
    return Array(CAROUSEL_STATION_COUNT_SLIDES).fill(soleParam).fill(otherParam, startPos);
};

/**
 * Получить объект с опорными массивами для построения динамической сетки (row, col)
 *
 * @function
 * @name utils.getReferenceDataArray
 * @param {number} maxCountYandexCard - максимальное кол-во карточек ЯСтанций
 * @returns {Record<string, number[]}
 */
export const getReferenceDataArray = (maxCountYandexCard: number): Record<string, number[]> => {
    const allowEmptyArrayCorrection = 1;
    const referenceArrayLength = maxCountYandexCard + allowEmptyArrayCorrection;
    const initDataValue = 0;
    const columnCount = 4;
    const maxColumnCountPos = 4;
    const row = {
        firstRowPos: 1,
        secondRowPos: 5,
        thirdRowPos: 10,
        oneRow: 1,
        twoRow: 2,
        threeRow: 3,
    };

    return {
        row: Array(referenceArrayLength).fill(initDataValue).fill(row.oneRow, row.firstRowPos)
            .fill(row.twoRow, row.secondRowPos).fill(row.threeRow, row.thirdRowPos),
        column: [...Array(referenceArrayLength)].map((_, index) => index++).fill(columnCount, maxColumnCountPos),
    };
};

/**
 * Получить url картинки для карусели секции умная колонка в зависимости от разрешения
 *
 * @function
 * @name utils.getCarouselImgUrl
 * @param {string} url - базовый урл
 * @param {string} id - порядковый номер блока
 * @param {".jpg"} type - расширение картинки
 * @param {Record<string, boolean>} mediaData - объект с данными о разрешении
 * @returns {string}
 */
export const getCarouselImgUrl = ({ url, id, type, mediaData }: TCarouselImgUrl): string => {
    type TKey = keyof typeof mediaData;
    for (const key in mediaData) {
        if (mediaData[key as TKey]) {
            return `${url + key + '-' + id + type}`;
        }
    }
    return `${url + id + type}`;
};

/**
 * Функция сравнения для метода сортировки yandexStationData (Сортирует карточках ЯСтанций по value от 1 до 4 по порядку)
 *
 * @function
 * @name utils.sortFunctionByType
 * @param {IShopItem} a - сравниваемый элемент
 * @param {IShopItem} b - сравниваемый элемент
 * @returns {number}
 */
export const sortFunctionByType = (a: IEquipment, b: IEquipment): number => {
    const unFilteredValue = 0;
    const firstElem = a as TSort;
    const secondElem = b as TSort;
    const [arrFirstElem] = firstElem.props;
    const [arrSecondElem] = secondElem.props;
    if (!arrFirstElem?.value || !arrSecondElem?.value) {
        return unFilteredValue;
    }
    /** Ввиду того, что value приходит в строковом формате - '[1]' | '[2]' и тд. */
    const secondSymbol = 1;
    return Number(arrFirstElem.value[secondSymbol]) - Number(arrSecondElem.value[secondSymbol]);
};

/**
 * Функция сравнения для метода сортировки yandexStationData (Сортирует карточках ЯСтанций по параметру sort)
 *
 * @function
 * @name utils.sortFunctionBySort
 * @param {IShopItem} a - сравниваемый элемент
 * @param {IShopItem} b - сравниваемый элемент
 * @returns {number}
 */
export const sortFunctionBySort = (a: IEquipment, b: IEquipment): number => {
    const firstElem = typeof a.sort === 'number' ? a.sort : 1000;
    const secondElem = typeof b.sort === 'number' ? b.sort : 1000;
    return firstElem - secondElem;
};

/**
 * Функция отправки аналитики
 *
 * @function
 * @name utils.dataLayerPushWrap
 * @param {(ref: TRef) => void} [callback] - колбек функция
 * @param {string} label - дополнительные свойства события как название акции/партнёрского сервиса/продукта и тд.
 * @param {string} [action] - тип действия события
 * @returns {void}
 */
export const dataLayerPushWrap = ({ callback, label, action = ACTIONS.clickOnBtn }: TDataLayer): void => {
    callback && callback();
    dataLayerPush({
        event: EVENTS.UAevent,
        category: CATEGORIES.landing,
        action,
        label,
    });
};
